<script setup lang="ts">
  import { ref, onMounted, onUnmounted, type PropType, watchEffect, computed } from 'vue'
  import AppContextMenu from '@/components/AppContextMenu.vue'
  import type { Image } from '@/types/image'
  import type { CSSProperties } from 'vue'

  const defaultImg = 'https://static.zaohaowu.com/fe-static/community/common/image-placeholder.png'

  // 定义组件的 props
  const props = defineProps({
    src: {
      type: String,
      required: true
    },
    alt: {
      type: String,
      required: false
    },
    title: {
      type: String,
      required: false
    },
    imageId: {
      type: String,
      required: false,
      default: ''
    },
    topicId: {
      type: String,
      required: false,
      default: ''
    },
    fullscreen: {
      type: String,
      required: false
    },
    previewOnClick: {
      // 控制“点击图片本体是否直接打开全屏”，方便外部自己控制打开全屏
      type: Boolean,
      default: true
    },
    allFullScreen: {
      type: Array as PropType<Image[]>,
      default: () => [],
      required: false
    },
    zoomOut: {
      type: Boolean,
      default: false
    },
    showIcon: {
      type: Boolean,
      default: true
    },
    useBrandIcon: {
      type: Boolean,
      default: false
    },
    useSkeleton: {
      type: Boolean,
      default: true
    },
    useFallbackImage: {
      // 加载失败的时候是否需要兜底图
      type: Boolean,
      default: false
    },
    objectFit: {
      type: String,
      default: 'contain'
    },
    needRoundCorner: {
      type: Boolean,
      default: false
    },
    customStyle: {
      type: Object as PropType<CSSProperties>,
      default: () => ({})
    },
    lazyLoad: {
      type: Boolean,
      default: false
    }
  })

  const emit = defineEmits(['onload'])

  // 定义响应式状态
  const loaded = ref(false)
  const error = ref(false)
  const imgSrc = ref('')
  const imageContainerRef = ref<HTMLDivElement>()
  const isIntersecting = ref(false)
  let observer: IntersectionObserver | null = null

  const loadImage = () => {
    const img = new Image()
    img.src = props.src

    // 监听图像加载成功事件
    img.onload = () => {
      imgSrc.value = props.src
      loaded.value = true
      error.value = false
      const imgWidth = img.width
      const imgHeight = img.height

      // 不是放大状态回调
      !props.zoomOut && emit('onload', { width: imgWidth, height: imgHeight })
      if (!imageContainerRef.value) {
        return
      }
      if (props.objectFit === 'contain' && props.needRoundCorner) {
        const ratio = imgWidth / imgHeight
        if (ratio >= 1) {
          imageContainerRef.value.style.cssText = `
            width:auto;
            height: auto;
            max-height: 100%;
            aspect-ratio: ${ratio};
          `
        } else {
          imageContainerRef.value.style.cssText = `
            width: auto;
            height: 100%;
            aspect-ratio: ${ratio};
          `
        }
      }
    }

    // 监听图像加载失败事件
    img.onerror = () => {
      loaded.value = false
      loaded.value = true
      error.value = true
    }
  }

  const imgShowGlobal = ref(false)
  const currentIndex = ref(0)
  const globalImg = () => {
    if (!props.fullscreen) return
    imgShowGlobal.value = true
    if (props.allFullScreen && props.allFullScreen.length > 0) {
      currentIndex.value = props.allFullScreen.findIndex(item => item.url === props.fullscreen)
    }
  }

  const prev = () => {
    if (!props.allFullScreen || props.allFullScreen.length <= 0) return
    currentIndex.value = currentIndex.value === 0 ? 0 : currentIndex.value - 1
  }

  const next = () => {
    if (!props.allFullScreen || props.allFullScreen.length <= 0) return
    currentIndex.value =
      currentIndex.value === props.allFullScreen.length - 1
        ? props.allFullScreen.length - 1
        : currentIndex.value + 1
  }

  // 有图片/话题标识时保留下载、复制右键菜单；否则仅使用普通容器承载同一份 viewer 内容。
  const fullscreenWrapper = computed(() =>
    props.imageId || props.topicId ? AppContextMenu : 'div'
  )

  const fullscreenWrapperProps = computed(() => {
    if (!props.imageId && !props.topicId) return {}

    return {
      topicId: props.topicId,
      imageId: props.imageId || props.allFullScreen[currentIndex.value]?.uniqueImageId || ''
    }
  })

  defineExpose({
    openFullscreen: globalImg
  })
  // 在组件挂载后加载图像
  onMounted(() => {
    if (!props.src) {
      // 没有 src 时直接显示错误状态
      loaded.value = true
      error.value = true
      return
    }

    if (props.lazyLoad && imageContainerRef.value) {
      // 使用 Intersection Observer 实现懒加载
      observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !isIntersecting.value) {
              isIntersecting.value = true
              loadImage()
              // 图片开始加载后，停止观察
              if (observer && imageContainerRef.value) {
                observer.unobserve(imageContainerRef.value)
              }
            }
          })
        },
        {
          rootMargin: '50px' // 提前 50px 开始加载
        }
      )
      observer.observe(imageContainerRef.value)
    } else {
      // 非懒加载模式，直接加载
      loadImage()
    }
  })

  onUnmounted(() => {
    if (observer) {
      observer.disconnect()
      observer = null
    }
  })

  watchEffect(() => {
    // 只在非懒加载模式下响应 src 变化
    if (!props.lazyLoad && props.src) {
      loadImage()
    }
  })

  const imageStyle = computed(() => ({
    objectFit: props.objectFit as any
  }))
</script>

<template>
  <div
    :class="['image-container', { 'fullscreen-image-container': props.zoomOut }]"
    :style="customStyle"
    ref="imageContainerRef"
  >
    <img
      v-show="!error"
      @click="props.previewOnClick && globalImg()"
      :class="[
        'loaded-img',
        { pointer: props.fullscreen && props.previewOnClick },
        { 'global-zoom-out': props.zoomOut }
      ]"
      v-if="loaded"
      :src="imgSrc"
      :alt="props.alt"
      :title="props.title"
      :style="imageStyle"
    />

    <div class="loaded-error" v-if="error">
      <img v-if="useFallbackImage" :src="defaultImg" />
      <span v-else class="hitems-img-failure"></span>
    </div>

    <!-- 如果图像正在加载，则显示加载提示 -->
    <div class="skeleton-item" :class="{ 'no-skeleton': !props.useSkeleton }" v-if="!loaded">
      <span
        v-if="showIcon"
        :class="[
          'skeleton-item-icon',
          useBrandIcon ? 'hitems-icon-placeholder' : 'hitems-skeleton-image'
        ]"
      ></span>
    </div>

    <!-- 全屏大图 -->
    <teleport to="body" v-if="imgShowGlobal">
      <div class="img-global-mask" @click.stop="imgShowGlobal = false">
        <a class="img-global-close hitems-close" @click="imgShowGlobal = false"></a>
        <component :is="fullscreenWrapper" v-bind="fullscreenWrapperProps">
          <template v-if="props.allFullScreen && props.allFullScreen.length > 0">
            <template v-for="(item, index) in props.allFullScreen" :key="item.url">
              <AppImage :zoomOut="true" v-if="index === currentIndex" :src="item.url" />
            </template>
            <a
              v-if="currentIndex > 0"
              :class="['img-global-btn', 'img-global-prev', 'hitems-arrow-left']"
              @click.stop="prev"
            ></a>
            <a
              v-if="currentIndex < props.allFullScreen.length - 1"
              :class="['img-global-btn', 'img-global-next', 'hitems-arrow-left']"
              @click.stop="next"
            ></a>
          </template>
          <AppImage v-else :zoomOut="true" :src="props.fullscreen || ''" />
        </component>
      </div>
    </teleport>
  </div>
</template>

<style scoped lang="stylus">
  @import './AppImage.styl'
</style>
